import React from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TWorkstationSelectProps } from '@/console/features/workstations/components/workstation-select/workstation-select.types';
import { TSelectOption } from '@/shared/components/ui/form/components/searchable-select/base-searchable-select.types';
import { AsyncSearchableSelect } from '@/shared/components/ui/form/components/searchable-select/variant/async/async-searchable-select';
import { useReadAllWorkstationsQuery } from '@/shared/redux/api/assets/workstations/workstations-api';
import {
  ReadAllWorkstationsQuery,
  WorkstationFragment,
} from '@/shared/types/types.generated';

export const WorkstationSelect = <
  TFieldValues extends FieldValues,
  TOptionShape = string,
>({
  name,
  workstationFilter,
  disableTags = true,
  createOption,
  getItemId,
  additionalOptions = [],
  onSelectChange,
  required,
  rtkQueryArgs,
  multiple = true,
  ...rest
}: TWorkstationSelectProps<TFieldValues, TOptionShape>) => {
  const { t } = useTranslation();

  const createDefaultOption = (workstation: WorkstationFragment) =>
    ({
      label: workstation.name,
      value: workstation.workstationId,
    }) as TSelectOption<TOptionShape>;

  const _createOption = createOption ?? createDefaultOption;

  const transformData = (data?: ReadAllWorkstationsQuery) => {
    if (!data) {
      return [];
    }

    const workstations = data.readAllWorkstationsForUser.items;
    const validWorkstations = workstationFilter
      ? workstations.filter(workstationFilter)
      : workstations;
    const options = validWorkstations.map(_createOption);

    return [...additionalOptions, ...options];
  };

  return (
    <AsyncSearchableSelect<TFieldValues, ReadAllWorkstationsQuery, TOptionShape>
      name={name}
      label={t('common:workstations')}
      placeholder={t('sra:search_workstations')}
      disableTags={disableTags}
      multiple={multiple}
      getItemId={getItemId}
      optionProps={{
        useRTKQuery: useReadAllWorkstationsQuery,
        transformData,
        rtkQueryArgs,
      }}
      onSelectChange={onSelectChange}
      required={required}
      {...rest}
    />
  );
};
